import { cacheKeys, useWorkspaceAsset, useWorkspaceRoutine } from '@gripp/shared-logic';
import { PrintIcon } from '@gripp/shared-ui';
import { useIsFetching } from '@tanstack/react-query';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable } from 'react-native';
import { useReactToPrint } from 'react-to-print';
import { RoutinePrintSheet } from './routinePrintSheet';

/**
 * The sheet has to be in the document with real layout for `react-to-print` to
 * clone it, so it is moved off-screen rather than hidden: `display: none` has
 * no box to measure and prints blank.
 */
const OFFSCREEN: CSSProperties = {
  position: 'fixed',
  left: '-10000px',
  top: 0,
  // Keeps the sheet from being announced or tabbed into while it sits there.
  visibility: 'hidden',
  pointerEvents: 'none',
};

type RoutinePrintButtonProps = {
  routineId: string;
  assetId: string;
};

/**
 * Prints a routine's blank form straight from the page the icon sits on: no
 * intermediate preview, just the browser's own print dialog.
 *
 * The sheet is mounted only once the icon is pressed, because expanding it
 * resolves every nested routine the form references -- work no one should pay
 * for by merely opening a routine.
 */
export const RoutinePrintButton = ({ routineId, assetId }: RoutinePrintButtonProps) => {
  const { t } = useTranslation();
  const [isArmed, setIsArmed] = useState(false);
  const [hasArmSettled, setHasArmSettled] = useState(false);
  const [hasSheetSettled, setHasSheetSettled] = useState(false);
  const hasFiredRef = useRef(false);

  const fetchedRoutine = useWorkspaceRoutine(isArmed ? routineId : '');
  /**
   * `useWorkspaceRoutine` mirrors its query into state through an effect, so
   * if `routineId` changes while armed, the hook can still hold the previous
   * id's routine for a frame. Only trust it once it matches what's armed.
   */
  const routine = fetchedRoutine?.id === routineId ? fetchedRoutine : undefined;
  const { workspaceAsset, isLoading: isAssetLoading } = useWorkspaceAsset(
    assetId,
    !isArmed || !assetId
  );

  /**
   * Counts the routine's own lookup and every nested one the sheet triggers.
   * Printing while one is in flight yields a form with a field missing.
   */
  const lookupsInFlight = useIsFetching({
    queryKey: cacheKeys('WorkspaceRoutine').details(),
  });

  const sheetRef = useRef<HTMLDivElement>(null);
  const disarm = useCallback(() => {
    hasFiredRef.current = false;
    setHasArmSettled(false);
    setHasSheetSettled(false);
    setIsArmed(false);
  }, []);
  const handlePrint = useReactToPrint({
    contentRef: sheetRef,
    documentTitle: routine?.config?.title || routine?.name,
    onAfterPrint: disarm,
  });

  /**
   * Used only to detect "the lookup settled and nothing came back" below --
   * not print readiness. A routine that never resolves must not leave the
   * control stuck showing a spinner forever.
   */
  useEffect(() => {
    if (!isArmed) return;
    const id = setTimeout(() => setHasArmSettled(true), 0);
    return () => clearTimeout(id);
  }, [isArmed]);

  /**
   * Deferred from the moment the sheet actually mounts (routine truthy), not
   * from the press: on the render that mounts `RoutinePrintSheet`, its nested
   * `useWorkspaceRoutine` lookups haven't subscribed yet, so `lookupsInFlight`
   * still reads zero and printing then would catch the form half-built.
   * Waiting a macrotask past that mount lets those subscriptions register
   * before the in-flight count is trusted. Keyed on the boolean rather than
   * the `routine` object so a changing object identity can't restart the
   * timer every render and starve it.
   *
   * Arming is part of the key, not just the routine: `useWorkspaceRoutine`
   * keeps its last mirrored value when the id goes empty, so disarming does
   * not make this false. Keyed on the routine alone, a second press would
   * never re-run this, leaving the flag that disarming cleared stuck false --
   * a control that spins forever once the first dialog has been dismissed.
   */
  const hasRoutine = !!routine;
  useEffect(() => {
    if (!isArmed || !hasRoutine) {
      setHasSheetSettled(false);
      return;
    }
    const id = setTimeout(() => setHasSheetSettled(true), 0);
    return () => clearTimeout(id);
  }, [isArmed, hasRoutine]);

  const isReady =
    isArmed &&
    hasSheetSettled &&
    !!routine &&
    lookupsInFlight === 0 &&
    (!assetId || !isAssetLoading);

  useEffect(() => {
    if (!isReady || hasFiredRef.current) return;
    hasFiredRef.current = true;
    handlePrint();
  }, [isReady, handlePrint]);

  /**
   * A failed or empty routine lookup leaves `routine` undefined forever, so
   * `isReady` never becomes true and `onAfterPrint` never runs. Without this,
   * the only print control is left disabled behind a spinner for the life of
   * the page. Release it once the lookup has had its chance to settle so the
   * user can press again.
   */
  useEffect(() => {
    if (!isArmed || !hasArmSettled || routine || lookupsInFlight !== 0) return;
    disarm();
  }, [isArmed, hasArmSettled, routine, lookupsInFlight, disarm]);

  return (
    <>
      <Pressable
        onPress={() => setIsArmed(true)}
        disabled={isArmed}
        accessibilityRole="button"
        accessibilityLabel={t('routine.print.button', 'Print')}
        testID="asset-routine-submit-print-button"
      >
        {isArmed && !isReady ? (
          <ActivityIndicator size="small" testID="asset-routine-submit-print-pending" />
        ) : (
          <PrintIcon size={24} />
        )}
      </Pressable>

      {isArmed ? (
        <div style={OFFSCREEN} aria-hidden="true">
          {routine ? (
            <RoutinePrintSheet
              ref={sheetRef}
              routine={routine}
              asset={workspaceAsset}
            />
          ) : null}
        </div>
      ) : null}
    </>
  );
};
